import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useApi, useSession } from '../auth/session.tsx';
import { ErrorLine } from '../components/ui.tsx';

const ROLE_WORDS = { owner: 'an owner', editor: 'an editor', viewer: 'a viewer' } as const;

/** Opening an invitation link: shows what it offers and lets the invited person join. */
export function InvitePage() {
  const { token = '' } = useParams();
  const api = useApi();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const preview = useQuery({ queryKey: ['invitation', token], queryFn: () => api.previewInvitation(token), retry: false });
  const accept = useMutation({
    mutationFn: () => api.acceptInvitation(token),
    onSuccess: async (workspace) => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(`/w/${workspace.id}/files`, { replace: true });
    },
  });
  const invitation = preview.data;
  return (
    <div className="signin">
      <div className="signin-sheet">
        <h1 className="page-title">Invitation</h1>
        {preview.isLoading && <p className="meta">Checking the invitation…</p>}
        {preview.isError && (
          <p>
            <span className="state">This invitation link is not valid.</span> Ask the person who invited you for a new one.
          </p>
        )}
        {invitation && invitation.status === 'pending' && invitation.forYou && (
          <>
            <p>
              {invitation.invitedBy ?? 'Someone'} invited you to <strong>{invitation.workspaceName}</strong> as {ROLE_WORDS[invitation.role]}.
            </p>
            <div className="modal-actions">
              <button className="button button-primary" disabled={accept.isPending} onClick={() => accept.mutate()}>
                Join {invitation.workspaceName}
              </button>
            </div>
            <ErrorLine error={accept.error} />
          </>
        )}
        {invitation && invitation.status === 'pending' && !invitation.forYou && (
          <>
            <p>
              <span className="state">This invitation is for {invitation.email}.</span> Sign in with that address to accept it.
            </p>
            <div>
              <button className="button" onClick={() => session.signOut()}>
                Sign out
              </button>
            </div>
          </>
        )}
        {invitation && invitation.status !== 'pending' && (
          <p>
            <span className="state">
              {invitation.status === 'expired' ? 'This invitation has expired.' : invitation.status === 'accepted' ? 'This invitation was already used.' : 'This invitation was withdrawn.'}
            </span>{' '}
            Ask {invitation.invitedBy ?? 'the person who invited you'} for a new one.
          </p>
        )}
      </div>
    </div>
  );
}
